import type { MealType, VisitWindow } from "./types";

/**
 * Visit-window time ranges expressed as "minutes since midnight".
 * Matches V&A's existing Neighbourhood Guide marketing framing.
 */
export interface TimeRange {
  start: number;
  end: number;
}

export const VISIT_WINDOWS: Record<VisitWindow, TimeRange> = {
  morning: { start: 9 * 60, end: 12 * 60 + 30 }, // 09:00 – 12:30
  afternoon: { start: 12 * 60 + 30, end: 17 * 60 }, // 12:30 – 17:00
  evening: { start: 17 * 60, end: 22 * 60 }, // 17:00 – 22:00
  fullDay: { start: 9 * 60, end: 21 * 60 }, // 09:00 – 21:00 (morning to sundown — covers dinner)
};

/**
 * Default meal-slot windows. Editable in Sanity post-launch.
 * The Slotter only fills meal preferences whose window overlaps
 * the chosen visit window — there are no silent swaps.
 */
export const MEAL_WINDOWS: Record<MealType, TimeRange> = {
  coffee: { start: 9 * 60, end: 10 * 60 + 30 }, // 09:00 – 10:30
  snack: { start: 15 * 60 + 30, end: 16 * 60 + 30 }, // 15:30 – 16:30
  lunch: { start: 12 * 60 + 30, end: 14 * 60 + 30 }, // 12:30 – 14:30
  dinner: { start: 18 * 60 + 30, end: 20 * 60 + 30 }, // 18:30 – 20:30
  drinks: { start: 17 * 60, end: 19 * 60 }, // 17:00 – 19:00
};

/**
 * Per-window algorithmic parameters.
 *  - `targetStopCount`: the Budgeter trims toward this number
 *  - `walkToleranceMinutes`: max walk between adjacent stops the
 *    Sequencer will accept before re-ordering
 */
export interface WindowParams {
  targetStopCount: number;
  walkToleranceMinutes: number;
}

export const WINDOW_PARAMS: Record<VisitWindow, WindowParams> = {
  morning: { targetStopCount: 4, walkToleranceMinutes: 5 },
  afternoon: { targetStopCount: 4, walkToleranceMinutes: 7 },
  evening: { targetStopCount: 3, walkToleranceMinutes: 7 },
  fullDay: { targetStopCount: 8, walkToleranceMinutes: 10 },
};

/** Average pedestrian speed used to convert distance → time. */
export const WALKING_SPEED_KMH = 5;

/**
 * Interest slug emitted when the visitor selects shopping in the wizard.
 * Stores only enter a plan when this is present in `inputs.interests`.
 */
export const SHOPPING_INTEREST = "shopping";

/**
 * English headline of the services `storeCategory`. Stores carrying
 * this category (banks, couriers, tailors) never enter a plan.
 */
export const SERVICES_STORE_CATEGORY = "Services";

/**
 * Default dwell when a CMS record lacks `dwellTimeMinutes`.
 * Matches the indexer's default; duplicated here so the engine
 * is self-contained against bad inputs.
 */
export const DEFAULT_DWELL_MINUTES = { min: 30, max: 60 } as const;

export const DEFAULT_SCORING_WEIGHTS = {
  interestMatch: 1,
  tagMatch: 1,
  popularity: 1,
  accessibilityBoost: 1,
} as const;

/**
 * District allow-list for the V&A precinct. The Place Indexer filters
 * its universe query to documents whose `district` reference matches
 * one of these (by English headline).
 *
 * Districts authored under different names (or future marina districts)
 * naturally fall out — keeping the engine scoped to waterfront content
 * without needing a `site` discriminator on every doc.
 */
export const WATERFRONT_DISTRICT_NAMES = [
  "Victoria Wharf",
  "Silo District",
  "Clock Tower District",
  "Breakwater Point",
  "Canal District",
  "Watershed",
  "Quays District",
  "Pierhead District",
  "Portswood Ridge District",
] as const;

/**
 * Max stops per plan carrying each category (English headline). Long,
 * ticketed excursions shouldn't repeat in one day — nearest-neighbour
 * sequencing otherwise chains them, since operators cluster on the same
 * quays. Categories not listed are uncapped, so themed days (e.g. three
 * heritage sites) remain possible.
 */
export const EXCURSION_CATEGORY_CAPS: Record<string, number> = {
  "On the Water": 1,
  "In the Air": 1,
  "Tours & Orientation": 1,
};
